import { useState } from 'react';
import { Loader2, Search, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

export function PropertyBookingsDumpPanel() {
  const [propertyId, setPropertyId] = useState('');
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runDump = async () => {
    if (!propertyId.trim()) {
      toast.error('Enter a property ID');
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.propertyBookingsDump({
        property_id: propertyId.trim(),
        year,
        month,
      });
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const summary = results?.summary;
  const propRecord = results?.property_record;
  const nameMapping = results?.name_to_property_id_mapping;
  const hasMismatch = summary && summary.total_bookings_found_by_property_id === 0 && summary.total_bookings_found_by_name > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5" />
          Property Bookings Dump (Field Discovery)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">
              Property ID
            </label>
            <input
              type="text"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              placeholder="e.g. 249304"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm w-48"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={runDump} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run Dump
          </Button>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Diagnosis banner */}
            {summary && (
              <div className={`rounded-lg border p-3 ${hasMismatch ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : 'bg-muted/30'}`}>
                {hasMismatch && <AlertTriangle className="h-4 w-4 text-red-500 inline mr-1.5" />}
                <span className="text-sm font-medium">{summary.diagnosis}</span>
              </div>
            )}

            {/* Property record */}
            {propRecord && (
              <div className="rounded-lg border p-3 bg-muted/30">
                <p className="text-sm font-medium mb-1">Property record ({propRecord.collection})</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  {Object.entries(propRecord).filter(([k]) => k !== 'collection').map(([k, v]) => (
                    <div key={k}>
                      <span className="text-muted-foreground">{k}: </span>
                      <span className="font-mono">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Name-to-property_id mapping (the key finding) */}
            {nameMapping && Object.keys(nameMapping).length > 0 && (
              <div className={`rounded-lg border p-3 ${hasMismatch ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                <p className="text-sm font-medium mb-2">
                  Actual property_id values on bookings matching this property name
                </p>
                {Object.entries(nameMapping).map(([col, pids]) => (
                  <div key={col} className="mb-2">
                    <p className="text-xs text-muted-foreground mb-1">{col}:</p>
                    <div className="flex flex-wrap gap-2">
                      {pids.map((p, i) => (
                        <Badge
                          key={i}
                          variant={String(p.value) === results.search_property_id ? 'default' : 'destructive'}
                          className="font-mono text-xs"
                        >
                          {String(p.value)} ({p.type})
                          {String(p.value) === results.search_property_id && ' = expected'}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Per-collection results */}
            {results.results_by_collection && Object.entries(results.results_by_collection).map(([colName, colData]) => {
              const hasHits = Object.keys(colData.count_by_field || {}).length > 0
                || Object.keys(colData.count_by_name || {}).length > 0;

              return (
                <div key={colName} className="rounded-lg border p-3 space-y-3">
                  <p className="text-sm font-medium flex items-center gap-2">
                    {colName}
                    {hasHits ? (
                      <Badge className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">HIT</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">NO MATCH</Badge>
                    )}
                  </p>

                  {/* Field counts */}
                  {Object.keys(colData.count_by_field || {}).length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Matches by field + value</p>
                      <div className="overflow-x-auto border rounded">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="text-left px-2 py-1 font-medium">Query</th>
                              <th className="text-right px-2 py-1 font-medium">Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(colData.count_by_field).map(([label, count]) => (
                              <tr key={label} className="border-b last:border-0">
                                <td className="px-2 py-1 font-mono text-[11px]">{label}</td>
                                <td className="px-2 py-1 text-right tabular-nums font-medium">{count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Name counts */}
                  {Object.keys(colData.count_by_name || {}).length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Matches by property_name</p>
                      <div className="overflow-x-auto border rounded">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="text-left px-2 py-1 font-medium">Query</th>
                              <th className="text-right px-2 py-1 font-medium">Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(colData.count_by_name).map(([label, count]) => (
                              <tr key={label} className="border-b last:border-0">
                                <td className="px-2 py-1 font-mono text-[11px]">{label}</td>
                                <td className="px-2 py-1 text-right tabular-nums font-medium">{count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Sample documents */}
                  {colData.sample_documents?.length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                        Sample documents ({colData.sample_documents.length}) via {colData.sample_source}
                      </summary>
                      <div className="mt-2 space-y-2">
                        {colData.sample_documents.map((doc, i) => (
                          <pre
                            key={i}
                            className="bg-muted/50 rounded p-2 overflow-x-auto text-[10px] leading-relaxed"
                          >
                            {JSON.stringify(doc, null, 2)}
                          </pre>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* No matches at all */}
                  {!hasHits && colData.any_field_hits && (
                    <p className="text-xs text-muted-foreground">
                      Broad $or scan: {colData.any_field_hits.$or_across_all_fields ?? 0} hits
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
